import React, {useEffect, useMemo, useState} from "react";
import {Link, useSearchParams} from "react-router-dom";
import {ArrowRight, ChevronRight} from "lucide-react";

import {Button} from "../components/ui/button";
import {pageSeo, productBrands as fallbackProductBrands, siteInfo} from "../data";
import {getProductBrands} from "../cms/productQueries";

function setMetaTag(selector, attributes) {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
}

function setCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
}

function setStructuredData(data) {
  const scriptId = "higa-page-structured-data";
  let script = document.getElementById(scriptId);

  if (!script) {
    script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [productBrands, setProductBrands] = useState(fallbackProductBrands);
  const [cmsStatus, setCmsStatus] = useState("loading");

  const requestedBrand = searchParams.get("category");

  useEffect(() => {
    let isMounted = true;

    async function loadProducts() {
      try {
        const cmsBrands = await getProductBrands();

        if (isMounted && Array.isArray(cmsBrands) && cmsBrands.length > 0) {
          setProductBrands(cmsBrands);
          setCmsStatus("ready");
          return;
        }

        if (isMounted) {
          setCmsStatus("fallback");
        }
      } catch (error) {
        console.error("Unable to load products from Sanity:", error);

        if (isMounted) {
          setCmsStatus("fallback");
        }
      }
    }

    loadProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedBrand = productBrands.find(
    (brand) => brand.slug === requestedBrand,
  );

  const activeBrand = selectedBrand || productBrands[0];

  const brandProducts = useMemo(
    () => activeBrand?.products || [],
    [activeBrand],
  );

  useEffect(() => {
    if (!activeBrand) {
      return;
    }

    const canonical = `${siteInfo.url}/products?category=${activeBrand.slug}`;
    const title = `${activeBrand.title} Industrial Products | HIG Automation`;
    const description = `${activeBrand.description} Explore representative ${activeBrand.title} product families available through HIG Automation.`;

    document.title = title;

    setMetaTag('meta[name="description"]', {
      name: "description",
      content: description,
    });

    setMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: "website",
    });

    setMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: title,
    });

    setMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });

    setMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: canonical,
    });

    setMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: brandProducts[0]?.image || `${siteInfo.url}/higa-logo.png`,
    });

    setCanonical(canonical);

    setStructuredData({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${activeBrand.title} Product Families`,
      description,
      url: canonical,
      itemListElement: brandProducts.map((product, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.title,
          description: product.description,
          image: product.image,
          brand: {
            "@type": "Brand",
            name: activeBrand.title,
          },
          category: product.category?.title || product.subtitle,
        },
      })),
    });
  }, [activeBrand, brandProducts]);

  const selectBrand = (brandSlug) => {
    setSearchParams({category: brandSlug});
  };

  if (!activeBrand) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-4xl font-black text-[#0a1929] mb-4">
            Product Catalog Unavailable
          </h1>
          <p className="text-slate-600">
            Please contact our sales team for product support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden bg-[#0a1929] text-white">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1800&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1929] via-[#0a1929]/95 to-[#0a1929]/70" />

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="text-[#ff6b7a] uppercase tracking-[0.3em] text-xs font-bold mb-4">
            Industrial Products
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
            Automation Product Portfolio
          </h1>

          <p className="max-w-3xl text-slate-300 text-lg leading-relaxed">
            Browse our product portfolio by brand. Product content is managed
            through our catalog system so product families, descriptions and
            images can be updated as our portfolio grows.
          </p>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {productBrands.map((brand) => {
              const isActive = brand.slug === activeBrand.slug;

              return (
                <button
                  key={brand.slug}
                  type="button"
                  onClick={() => selectBrand(brand.slug)}
                  className={`text-left border p-5 transition-all ${
                    isActive
                      ? "border-[#c8102e] bg-[#0a1929] text-white shadow-lg"
                      : "border-slate-200 bg-white hover:border-[#c8102e]"
                  }`}
                >
                  <div
                    className={`text-lg font-black uppercase tracking-wide mb-2 ${
                      isActive ? "text-white" : "text-[#0a1929]"
                    }`}
                  >
                    {brand.title}
                  </div>

                  <div
                    className={`text-sm leading-relaxed ${
                      isActive ? "text-slate-300" : "text-slate-500"
                    }`}
                  >
                    {brand.menuSummary}
                  </div>
                </button>
              );
            })}
          </div>

          {cmsStatus === "fallback" && (
            <p className="text-xs text-slate-400 mt-4">
              Catalog backup data is currently being displayed.
            </p>
          )}
        </div>
      </section>

      <section className="py-14 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <div className="text-[#c8102e] uppercase tracking-[0.3em] text-xs font-bold mb-3">
              Product Brand
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0a1929] tracking-tight mb-4">
              {activeBrand.title}
            </h2>

            <p className="text-slate-600 text-lg leading-relaxed">
              {activeBrand.description}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-[1500px] mx-auto px-6">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-7">
            {brandProducts.map((product) => (
              <article
                key={product.slug}
                className="group border border-slate-200 bg-white shadow-sm hover:shadow-xl hover:border-[#c8102e] transition-all overflow-hidden flex flex-col"
              >
                <div className="h-56 bg-slate-100 flex items-center justify-center overflow-hidden p-5">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={`${activeBrand.title} ${product.title}`}
                      className="max-w-full max-h-full w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-center text-slate-400 text-sm px-4">
                      Product image coming soon
                    </div>
                  )}
                </div>

                <div className="p-6 flex flex-col flex-1 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#c8102e] font-bold mb-2">
                    {activeBrand.title}
                  </div>

                  <h2 className="text-lg font-black text-[#0a1929] leading-snug mb-2">
                    {product.title}
                  </h2>

                  <p className="text-sm text-slate-500 mb-5">
                    {product.subtitle || product.category?.title}
                  </p>

                  <p className="text-sm text-slate-600 leading-relaxed mb-6">
                    {product.description || product.shortDescription}
                  </p>

                  <Link
                    to="/contact"
                    className="mt-auto inline-flex items-center justify-between border border-[#c8102e] text-[#c8102e] hover:bg-[#c8102e] hover:text-white px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] transition-colors"
                  >
                    Enquire Now
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {brandProducts.length === 0 && (
            <div className="border border-slate-200 bg-slate-50 p-10 text-center">
              <h2 className="text-2xl font-black text-[#0a1929] mb-3">
                Products Coming Soon
              </h2>

              <p className="text-slate-600">
                Contact our sales team for the latest {activeBrand.title} product
                availability.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#0a1929] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <div className="text-[#ff6b7a] uppercase tracking-[0.3em] text-xs font-bold mb-3">
              Product Support
            </div>

            <h2 className="text-3xl lg:text-4xl font-black mb-3">
              Need Help Selecting the Right Product?
            </h2>

            <p className="text-slate-300 max-w-2xl leading-relaxed">
              Share your machine, process or replacement requirement with our
              team. We can discuss the application and integration requirement.
            </p>
          </div>

          <Link to="/contact">
            <Button className="bg-[#c8102e] hover:bg-[#a80d26] text-white rounded-none px-8 py-6 font-bold uppercase">
              Contact Product Team
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
